// =============================================================
// collab.js — the Notes live co-editing client, wired for the web.
// The protocol lives ONCE in shared/src/noteCollab.ts (the iOS editor runs the
// same code); this file supplies its dependencies: Yjs, the awareness
// helpers, the API client, and BlockNote for converting a note's stored
// Content into its first Yjs state. No persistence on the web — the tab is
// online or it is not editing live.
// =============================================================
import * as Y from 'yjs';
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
import { BlockNoteEditor } from '@blocknote/core';
import { blocksToYDoc } from '@blocknote/core/yjs';
import { openNoteCollab as openShared, COLLAB_FRAGMENT, bytesToB64 } from '@harvus/shared';

/** Convert stored Content (BlockNote JSON or legacy HTML) into a seed Yjs update. */
export async function buildSeed(content) {
  const editor = BlockNoteEditor.create();
  let blocks = [];
  const t = String(content || '').trim();
  if (t.startsWith('[') && t.endsWith(']')) {
    try { const b = JSON.parse(t); if (Array.isArray(b)) blocks = b; } catch { /* empty */ }
  } else if (t) {
    try { blocks = await editor.tryParseHTMLToBlocks(t); } catch { blocks = [{ type: 'paragraph', content: t.replace(/<[^>]+>/g, ' ') }]; }
  }
  const doc = blocksToYDoc(editor, blocks, COLLAB_FRAGMENT);
  const bytes = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  return bytesToB64(bytes);
}

/** The API client as the shared core expects it: never throws, says whether the network failed. */
export const collabRequest = (api) => async (method, path, body) => {
  try {
    const res = method === 'GET' ? await api.get(path) : method === 'PUT' ? await api.put(path, body) : await api.post(path, body);
    return { ok: true, status: 200, body: res, network: false };
  } catch (e) {
    return { ok: false, status: e?.status || 0, body: e?.body || { error: e?.message }, network: !!(e?.isNetwork || e?.isTimeout) };
  }
};

export function openNoteCollab({ api, noteId, onStatus, onReset }) {
  return openShared({
    noteId,
    onStatus,
    onReset,
    onGenerationLost: onReset,
    deps: {
      Y,
      awareness: { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates },
      request: collabRequest(api),
      buildSeed,
    },
  });
}
